import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import PortalLayout from "../components/PortalLayout";
import { api, parseErrorDetail } from "../lib/api";

const CENTS = (c) => (c == null ? "—" : `$${(Math.abs(c) / 100).toFixed(2)}`);

export default function MyOrders() {
  const nav = useNavigate();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [busyRow, setBusyRow] = useState(null);
  const [rowError, setRowError] = useState({});

  const load = useCallback(async () => {
    setLoading(true); setError("");
    try {
      const r = await api.get("/orders/mine");
      setRows(r.data || []);
    } catch (err) {
      setError(parseErrorDetail(err));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const onCancelRow = useCallback(async (order_id, order_number) => {
    if (!window.confirm(`Cancel order ${order_number}? This cannot be undone here.`)) return;
    setBusyRow(order_id);
    setRowError((e) => ({ ...e, [order_id]: "" }));
    try {
      await api.post(`/orders/${order_id}/cancel`, { reason: null });
      await load();
    } catch (err) {
      setRowError((e) => ({ ...e, [order_id]: parseErrorDetail(err) }));
    } finally {
      setBusyRow(null);
    }
  }, [load]);

  return (
    <PortalLayout title="My orders">
      <section className="apwo-panel" data-testid="my-orders-panel">
        <div className="apwo-summary-head">
          <h2>My orders <span className="apwo-cell-muted">newest first</span></h2>
        </div>
        {loading && <p className="apwo-muted">Loading…</p>}
        {error && <div className="apwo-alert-error" data-testid="my-orders-error">{error}</div>}
        {!loading && !error && rows.length === 0 && (
          <p className="apwo-muted" data-testid="my-orders-empty">You have no orders yet.</p>
        )}
        {rows.length > 0 && (
          <table className="apwo-summary-table" data-testid="my-orders-table">
            <thead>
              <tr>
                <th>Order #</th>
                <th>Date</th>
                <th>Term</th>
                <th>Status</th>
                <th>Cancelled / Replaced</th>
                <th className="apwo-th-right">Reward applied</th>
                <th className="apwo-th-right">Total (incl. GST)</th>
                <th className="apwo-th-right">Reward earned</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.order_id} data-testid={`my-orders-row-${r.order_number}`}
                    className={r.cancelled_at ? "apwo-row-cancelled" : ""}>
                  <td>
                    <button
                      className="apwo-linky"
                      onClick={() => nav(`/portal/order/success/${r.order_id}`)}
                      data-testid={`my-orders-view-${r.order_number}`}
                    >
                      {r.order_number}
                    </button>
                  </td>
                  <td className="apwo-cell-muted">{new Date(r.created_at).toLocaleString()}</td>
                  <td data-testid={`my-orders-term-${r.order_number}`}>{r.payment_term || "—"}</td>
                  <td><span className="apwo-badge">{r.payment_status}</span></td>
                  <td data-testid={`my-orders-status-link-${r.order_number}`}>
                    {r.cancelled_at ? (
                      <span className="apwo-badge apwo-badge--danger">
                        Cancelled {r.cancelled_at_sydney ? `· ${new Date(r.cancelled_at_sydney).toLocaleString()}` : ""}
                      </span>
                    ) : "—"}
                    {r.replaces_order_id && <div className="apwo-cell-muted apwo-fineprint">replaces earlier order</div>}
                    {r.replaced_by_order_id && <div className="apwo-cell-muted apwo-fineprint">replaced by later order</div>}
                  </td>
                  <td className="apwo-th-right" data-testid={`my-orders-reward-applied-${r.order_number}`}>
                    {r.reward_applied_cents > 0 ? `-${CENTS(r.reward_applied_cents)}` : "—"}
                  </td>
                  <td className="apwo-th-right">{CENTS(r.payable_total_cents)}</td>
                  <td className="apwo-th-right" data-testid={`my-orders-earned-${r.order_number}`}>
                    {r.earned_reward_cents > 0 ? CENTS(r.earned_reward_cents) : "—"}
                  </td>
                  <td>
                    {r.is_cancellable && !r.cancelled_at && (
                      <button
                        className="apwo-linky"
                        disabled={busyRow === r.order_id}
                        onClick={() => onCancelRow(r.order_id, r.order_number)}
                        data-testid={`my-orders-cancel-${r.order_number}`}
                      >
                        {busyRow === r.order_id ? "Cancelling…" : "Cancel"}
                      </button>
                    )}
                    {rowError[r.order_id] && (
                      <div className="apwo-fineprint apwo-alert-error apwo-alert-error--inline" data-testid={`my-orders-row-error-${r.order_number}`}>
                        {rowError[r.order_id]}
                      </div>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </section>
    </PortalLayout>
  );
}
